/**
 * Sleeps for a specified number of milliseconds.
 * @param ms - The number of milliseconds to sleep.
 * @returns A promise that resolves after the delay.
 */
function waitSleeperFn(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Waits asynchronously until a condition is met, or a timeout occurs.
 *
 * @param conditionFn - A synchronous function that returns a boolean. The function is polled until it returns true.
 * @param checkInterval - How often to check the condition (in milliseconds). Defaults to 100ms.
 * @param timeout - Maximum time to wait before rejecting (in milliseconds). Defaults to 10,000ms (10s).
 * @returns A promise that resolves when the condition is met, or rejects on timeout.
 */
export async function waitUntil(
  conditionFn: () => boolean,
  checkInterval = 100,
  timeout = 10_000
): Promise<void> {
  const start = Date.now();

  while (!conditionFn()) {
    if (Date.now() - start > timeout) {
      throw new Error('Timeout waiting for condition');
    }
    await waitSleeperFn(checkInterval);
  }
}
